// Host selection for the delete Undo pill and protection notices.
//
// The notices render inside the surface the user is looking at (the topmost
// `aria-modal` dialog, or PrayPage's home container when none is open), so a
// NavStack overlay (z 1000+) never paints over them and a dialog's focus trap
// and `aria-modal` scope include them. "Topmost" uses the same rule as
// useDialogFocus, so the notice host and the focus trap always agree.
import { topmostModal } from '@/hooks/useDialogFocus';

const MODAL = '[role="dialog"][aria-modal="true"]';

/** Dialogs that must unmount before a deletion's notices choose a host. */
export interface NoticeSource {
  outgoing: readonly HTMLElement[];
}

/**
 * Capture the deleting surface (the prayer dialog and anything above it, such
 * as the leaf sheet) BEFORE its layer is closed. `minZ` is that layer's
 * coordinator z; every connected modal at or above it is outgoing.
 */
export function captureNoticeSource(minZ: number | undefined): NoticeSource {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>(MODAL));
  if (minZ === undefined) {
    const top = topmostModal();
    return { outgoing: top ? [top] : [] };
  }
  return {
    outgoing: dialogs.filter((dialog) => {
      const z = Number.parseInt(getComputedStyle(dialog).zIndex, 10);
      return Number.isFinite(z) && z >= minZ;
    }),
  };
}

export function sourceIsSettled(source: NoticeSource | null): boolean {
  return !source || source.outgoing.every((dialog) => !dialog.isConnected);
}

/** The dialog that owns `element`, or null for the home container. */
export function owningModal(element: HTMLElement): HTMLElement | null {
  return element.matches(MODAL) ? element : element.closest<HTMLElement>(MODAL);
}

export function resolveNoticeHost(home: HTMLElement | null): HTMLElement | null {
  return topmostModal() ?? home;
}

/** A host is covered when a different modal is now the topmost one. */
export function hostIsCovered(host: HTMLElement): boolean {
  return topmostModal() !== owningModal(host);
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * First visible focusable control in `host` outside every notice region (on
 * home the notices sit in several HomeFeedbackArea slots).
 */
export function firstFocusableOutside(host: HTMLElement): HTMLElement | null {
  for (const element of host.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    if (element.closest('[data-deletion-notices]')) continue;
    if (element.getAttribute('aria-hidden') === 'true') continue;
    if (element.getClientRects().length === 0) continue;
    return element;
  }
  return null;
}
